import type { Note, ScheduledCall, UserCourse } from '@teachur/http-client';
import { timestampToDate } from '@new/common/lib/dateTime';

export type CourseHearingReviewStatus = 'approved' | 'notYet' | undefined;

export interface CourseHearingProgressData {
  userCourse?: UserCourse | null;
  notes?: Note[] | null;
}

export interface CourseHearingSummary {
  latestStatus: string;
}

export const getLatestScheduledCall = (scheduledCalls: ScheduledCall[]) => {
  return scheduledCalls
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
};

const MISSED_CUTOFF_MS = 8 * 60 * 60 * 1000;

export const isScheduledCallMissed = (
  call: Pick<ScheduledCall, 'status' | 'startTime'> | undefined,
  videoCallEndedAt: string | null | undefined,
  attemptsLoaded: boolean
): boolean => {
  if (!call || call.status !== 'accepted') return false;
  if (!attemptsLoaded) return false;
  if (videoCallEndedAt) return false;

  const missedThreshold = timestampToDate(call.startTime).getTime() + MISSED_CUTOFF_MS;
  return Date.now() > missedThreshold;
};

export const getMissedCutoffTime = (call: Pick<ScheduledCall, 'startTime'>): number => {
  return timestampToDate(call.startTime).getTime() + MISSED_CUTOFF_MS;
};

export const getCourseHearingReviewStatus = (
  reviewNotes: Note[],
  coursePassed: boolean
): CourseHearingReviewStatus => {
  if (coursePassed || reviewNotes.some((note) => note.approved)) {
    return 'approved';
  }

  if (reviewNotes.length > 0 && reviewNotes.every((note) => !note.approved)) {
    return 'notYet';
  }

  return undefined;
};

const getReviewStatusLabel = (status: CourseHearingReviewStatus) => {
  if (status === 'approved') return 'Approved';
  if (status === 'notYet') return 'Needs revision';

  return 'Not reviewed';
};

export const getCourseHearingSummary = (
  progressData?: CourseHearingProgressData | null
): CourseHearingSummary => {
  const notes = progressData?.notes ?? [];
  const coursePassed = Boolean(progressData?.userCourse?.passed);

  const reviewStatus = getCourseHearingReviewStatus(notes, coursePassed);

  return {
    latestStatus: getReviewStatusLabel(reviewStatus),
  };
};
